import { useRef, useState, type KeyboardEvent } from 'react';
import type { Effort, Mode, ModelAlias, Preset, ToolGroup } from '../lib/types';
import { TOOL_GROUPS } from '../lib/tools';

interface Props {
  presets: Preset[];
  presetIds: string[];
  effort: Effort;
  model: ModelAlias;
  mode: Mode;
  judge: boolean;
  toolGroups: ToolGroup[];
  streaming: boolean;
  onTogglePreset: (id: string) => void;
  onSetEffort: (v: Effort) => void;
  onSetModel: (v: ModelAlias) => void;
  onSetMode: (v: Mode) => void;
  onSetJudge: (v: boolean) => void;
  onToggleToolGroup: (id: ToolGroup) => void;
  onSend: (text: string) => void;
}

const EFFORTS: Effort[] = ['none', 'low', 'medium', 'high', 'max'];
const MODELS: ModelAlias[] = ['opus', 'sonnet', 'haiku'];
const MODES: { id: Mode; label: string }[] = [
  { id: 'single', label: 'single' },
  { id: 'perspectives', label: 'perspectives ×3' },
  { id: 'debate', label: 'debate ×3' },
];

export function Composer(props: Props) {
  const [text, setText] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  function autosize(): void {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }

  function submit(): void {
    const t = text.trim();
    if (!t || props.streaming) return;
    props.onSend(t);
    setText('');
    queueMicrotask(autosize);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="space-y-2 max-w-3xl mx-auto">
      {showSettings && (
        <div className="space-y-3 bg-gray-900 border border-gray-800 p-3 rounded-xl text-sm">
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2">
              <span className="text-gray-500">model</span>
              <select
                value={props.model}
                onChange={(e) => props.onSetModel(e.target.value as ModelAlias)}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 outline-none"
              >
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-gray-500">effort</span>
              <select
                value={props.effort}
                onChange={(e) => props.onSetEffort(e.target.value as Effort)}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 outline-none"
              >
                {EFFORTS.map((eff) => (
                  <option key={eff} value={eff}>
                    {eff}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-gray-500">mode</span>
              <select
                value={props.mode}
                onChange={(e) => props.onSetMode(e.target.value as Mode)}
                className="bg-gray-800 border border-gray-700 rounded px-2 py-1 outline-none"
              >
                {MODES.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            {(props.mode === 'perspectives' || props.mode === 'debate') && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={props.judge}
                  onChange={(e) => props.onSetJudge(e.target.checked)}
                  className="accent-blue-500"
                />
                <span className="text-gray-300">+ Судья</span>
              </label>
            )}
          </div>
          {props.mode === 'single' && (
            <div className="space-y-1">
              <div className="text-gray-500">инструменты</div>
              <div className="flex flex-wrap gap-2">
                {TOOL_GROUPS.map((g) => {
                  const on = props.toolGroups.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      title={g.hint}
                      onClick={() => props.onToggleToolGroup(g.id)}
                      className={`px-2 py-1 rounded-lg border text-sm transition flex items-center gap-1 ${
                        on
                          ? 'bg-emerald-600/20 border-emerald-500 text-emerald-200'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <span>{g.icon}</span>
                      <span>{g.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {props.presets.length > 0 && (
            <div className="space-y-1">
              <div className="text-gray-500">пресеты</div>
              <div className="flex flex-wrap gap-2">
                {props.presets.map((p) => {
                  const on = props.presetIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => props.onTogglePreset(p.id)}
                      className={`px-2 py-1 rounded-lg border text-sm transition ${
                        on
                          ? 'bg-blue-600/20 border-blue-500 text-blue-200'
                          : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {p.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <textarea
          ref={taRef}
          rows={1}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            autosize();
          }}
          onKeyDown={onKeyDown}
          placeholder="Сообщение…"
          className="flex-1 resize-none bg-gray-900 border border-gray-800 rounded-2xl px-4 py-2 outline-none focus:border-gray-600 max-h-52 text-[15px]"
        />
        <button
          type="button"
          onClick={() => setShowSettings(!showSettings)}
          className={`shrink-0 px-3 py-2 rounded-2xl border text-sm transition ${
            showSettings
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-gray-900 border-gray-800 text-gray-400 hover:bg-gray-800'
          }`}
        >
          параметры
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() || props.streaming}
          className="shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-800 disabled:text-gray-500 rounded-2xl font-medium transition"
        >
          {props.streaming ? '…' : 'отпр'}
        </button>
      </div>
    </div>
  );
}
